import React from 'react';
import { FilterOptions } from '../types';
import { Search, Plus, X, Archive } from 'lucide-react';

interface VehicleFilterBarProps {
  filters: FilterOptions;
  onFilterChange: (filters: FilterOptions) => void;
  onSearch: (searchTerm: string) => void;
  searchTerm: string;
  onAddVehicle?: () => void;
}

const VehicleFilterBar: React.FC<VehicleFilterBarProps> = ({ 
  filters, 
  onFilterChange, 
  onSearch, 
  searchTerm,
  onAddVehicle
}) => {
  const handleFilterChange = (key: keyof FilterOptions, value: string | boolean) => {
    onFilterChange({
      ...filters,
      [key]: value === '' ? undefined : value,
    });
  };

  const clearFilters = () => {
    onFilterChange({});
  };

  const hasActiveFilters = Object.values(filters).some(value => value !== undefined);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
      <div className="flex flex-col lg:flex-row gap-4">
        {/* Search */}
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Fahrzeuge suchen..."
              value={searchTerm}
              onChange={(e) => onSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#add015] focus:border-transparent bg-white text-gray-900 placeholder-gray-400"
            />
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3">
          {/* Manufacturer Filter */}
          <select
            value={filters.manufacturer || ''}
            onChange={(e) => handleFilterChange('manufacturer', e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
          >
            <option value="">Alle Hersteller</option>
            <option value="Volkswagen">Volkswagen</option>
            <option value="BMW">BMW</option>
            <option value="Mercedes">Mercedes</option>
            <option value="Audi">Audi</option>
            <option value="Škoda">Škoda</option>
            <option value="Ford">Ford</option>
            <option value="SEAT">SEAT</option>
            <option value="CUPRA">CUPRA</option>
            <option value="Jeep">Jeep</option>
          </select>

          {/* Type Filter */}
          <select
            value={filters.type || ''}
            onChange={(e) => handleFilterChange('type', e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
          >
            <option value="">Alle Typen</option>
            <option value="Kleinwagen">Kleinwagen</option>
            <option value="Kompaktklasse">Kompaktklasse</option>
            <option value="Mittelklasse">Mittelklasse</option>
            <option value="Oberklasse">Oberklasse</option>
            <option value="SUV">SUV</option>
            <option value="Van">Van</option>
            <option value="Transporter">Transporter</option>
          </select>

          {/* Poolcar Filter */}
          <select
            value={filters.poolcar === undefined ? '' : filters.poolcar ? 'true' : 'false'}
            onChange={(e) => handleFilterChange('poolcar', e.target.value === 'true')}
            className="px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
          >
            <option value="">Alle Fahrzeuge</option>
            <option value="true">Nur Poolcars</option>
                            <option value="false">Nur Firmenfahrzeuge</option>
          </select>

          {/* Versteuerungs-Filter */}
          <select
            value={filters.taxation || ''}
            onChange={(e) => handleFilterChange('taxation', e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
          >
            <option value="">Alle Versteuerungen</option>
            <option value="Keine Versteuerung">Keine Versteuerung</option>
            <option value="1% Verbrenner">1% Verbrenner</option>
            <option value="0,5% Hybrid">0,5% Hybrid</option>
            <option value="0,25% Elektro">0,25% Elektro</option>
          </select>

          {/* Vertragsende Filter */}
          <button
            onClick={() => handleFilterChange('showEnded', !filters.showEnded)}
            className={`px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent flex items-center gap-2 ${
              filters.showEnded
                ? 'bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-900 dark:border-blue-600 dark:text-blue-300'
                : 'bg-white border-gray-300 text-gray-700 dark:bg-gray-900 dark:border-gray-600 dark:text-gray-300'
            }`}
          >
            <Archive className="w-4 h-4" />
            {filters.showEnded ? 'Aktive Fahrzeuge' : 'Beendete Fahrzeuge'}
          </button>

          {/* Add Vehicle Button */}
          {onAddVehicle && (
            <button
              onClick={onAddVehicle}
              className="px-4 py-2 bg-[#add015] text-black rounded-lg hover:bg-[#add015] hover:bg-opacity-80 transition-colors font-semibold flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Fahrzeug hinzufügen
            </button>
          )}

          {/* Clear Filters Button */}
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="flex items-center space-x-1 px-3 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-4 h-4" />
              <span>Filter löschen</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default VehicleFilterBar; 